import React from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { CircleMarker, FeatureGroup, MapContainer, Polyline, Popup, ScaleControl, TileLayer } from "react-leaflet";
import { LeafletMouseEvent, PathOptions } from "leaflet";
import styled from "styled-components";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

import { useAppSelector } from "../../redux/hooks";
import variableColors from "../../_App.module.scss";
import { PopupContainer } from "../views/SelectedBusViewMap";
import { apiKey, apiPrefix } from "../../config";
import { dynamicTextColor } from "../../utils/helpers";
import { InfoPopup } from "../helpers/PolylinesComponents";
import { AgencyContext } from "../../hooks/AgencyContext";
import { StopAvailableRoutes } from "../helpers/Helpers";

const { primaryColor, dangerColor } = variableColors;

const DivMap = styled.div`
  width: 100%;
  height: 60vh;
`;

//* The Main Component
const StopPunctualityReportMapModal = ({
  selectedStopId,
  disabled,
  selectedStopRoutes,
}: {
  selectedStopId: string;
  disabled: boolean;
  selectedStopRoutes: RouteDetails[];
}): JSX.Element => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth0();

  const [allStopsArrayFiltered]: [StopInfo[]] = useAppSelector(
    (state: RootState) => [state?.routesInfo?.allStopsArrayFiltered],
    shallowEqual
  );

  //* AgencyContext
  const { agencyId, initialMapRange } = React.useContext(AgencyContext) as AgencyContextType;

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [show, setShow] = React.useState<boolean>(false);
  const [selectedStopRoutesIds, setSelectedStopRoutesIds] = React.useState<string[]>([]);
  const [routesData, setRoutesData] = React.useState<RouteObjReduced[]>([]);

  const closeAndReset = (): void => {
    handleClose();
    setSelectedStopRoutesIds([]);
    setRoutesData([]);
  };

  //* Find routes via selectedStop
  React.useEffect(() => {
    if (show && selectedStopRoutes && selectedStopId) {
      // console.log("selectedStopRoutes:", selectedStopRoutes);
      const selectedStopRoutesIdsToSet = selectedStopRoutes?.map((filteredRoute: RouteDetails) => filteredRoute?.id);
      // console.log("selectedStopRoutesIdsToSet:", selectedStopRoutesIdsToSet);
      setSelectedStopRoutesIds(selectedStopRoutesIdsToSet);
    }
  }, [selectedStopId, selectedStopRoutes, show]);

  //* Get routes details
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (show && selectedStopRoutesIds?.length) {
      // console.log("selectedStopRoutesIds:", selectedStopRoutesIds);

      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();

      const routesIds = selectedStopRoutesIds?.map((id: string) => `r=${id}&`);
      const stringOfIDs = routesIds?.join("")?.slice(0, -1);
      // console.log("stringOfIDs:", stringOfIDs);

      axios
        .get(`${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/command/routesDetails?${stringOfIDs}`)
        .then(
          async ({ data: { routes: routesList } }) => {
            const routeObjArray = [] as RouteObj[];
            for (let i = 0; i < routesList.length; i++) {
              let routeObj = {
                id: routesList[i]?.id as string,
                name: routesList[i]?.name as string,
                longName: routesList[i]?.longName as string,
                shortName: routesList[i]?.shortName as string,
                shapesDirection_0: null,
                shapesDirection_1: null,
                background: routesList[i]?.color ? (`#${routesList[i]?.color}` as string) : "#000000",
                color: routesList[i]?.color
                  ? dynamicTextColor(`#${routesList[i]?.color}` as string)
                  : dynamicTextColor("#000000"),
              };

              if (routesList[i]?.shape?.length >= 1) {
                const shapesDirection_0 = routesList[i]?.shape.filter(
                  (elem: { directionId: string }) => elem?.directionId === "0"
                );
                const shapesDirection_1 = routesList[i]?.shape.filter(
                  (elem: { directionId: string }) => elem?.directionId === "1"
                );
                routeObj = { ...routeObj, shapesDirection_0, shapesDirection_1 };
              }

              for (let i = 0; i < routeObjArray?.length; i++) {
                if (routeObjArray[i].shapesDirection_0 && routeObjArray[i].shapesDirection_0?.length) {
                  for (let j = 0; j < routeObjArray[i].shapesDirection_0!.length; j++) {
                    for (let k = 0; k < routeObjArray[i].shapesDirection_0![j].loc.length; k++) {
                      routeObjArray[i].shapesDirection_0![j].loc[k].lng = routeObjArray[i].shapesDirection_0![j].loc[k].lon;
                    }
                  }
                }
              }
              for (let i = 0; i < routeObjArray?.length; i++) {
                if (routeObjArray[i].shapesDirection_1 && routeObjArray[i].shapesDirection_1?.length) {
                  for (let j = 0; j < routeObjArray[i].shapesDirection_1!.length; j++) {
                    for (let k = 0; k < routeObjArray[i].shapesDirection_1![j].loc.length; k++) {
                      routeObjArray[i].shapesDirection_1![j].loc[k].lng = routeObjArray[i].shapesDirection_1![j].loc[k].lon;
                    }
                  }
                }
              }
              routeObjArray.push(routeObj);
            }

            const routeObjArrayFiltered = routeObjArray?.map((routeObj: RouteObj) => {
              return {
                id: routeObj?.id,
                shortName: routeObj?.shortName,
                background: routeObj?.background,
                color: routeObj?.color,
                shapesDirection_0: routeObj?.shapesDirection_0
                  ?.map((elem) => elem?.loc)
                  .filter((loc) => loc?.length >= 10)
                  .sort((a, b) => b?.length - a?.length),
                shapesDirection_1: routeObj?.shapesDirection_1
                  ?.map((elem) => elem?.loc)
                  .filter((loc) => loc?.length >= 10)
                  .sort((a, b) => b?.length - a?.length),
              };
            }) as RouteObjReduced[];
            setRoutesData(routeObjArrayFiltered);
          },
          (reason) => {
            if (reason) {
              console.log({ reason });
            }
          }
        )
        .catch((error) => {
          if (error.response) {
            console.error("error.response.status:", error.response.status);
          }
        });

      return () => {
        source.cancel();
      };
    }
  }, [agencyId, isAuthenticated, selectedStopRoutesIds, show]);

  //* Polyline via selectedStop
  const PolylineToRender = (
    routeObj: RouteObjReduced,
    directionSet: LanLngPoint[],
    index: number,
    polylineOptions: PathOptions
  ): JSX.Element => {
    return (
      <Polyline
        key={index + routeObj?.id}
        pathOptions={polylineOptions}
        positions={directionSet}
        children={
          <Popup>
            <InfoPopup $width={"auto"}>
              <p>
                {t("Id")}{" "}
                <span style={{ background: routeObj?.background, color: routeObj?.color, padding: "0.25rem 0.5rem" }}>
                  {routeObj?.shortName || routeObj?.id || t("b/d")}
                </span>
              </p>
            </InfoPopup>
          </Popup>
        }
      />
    );
  };

  //* Routes via selectedStop
  const allRoutesPolylinesViaStop: JSX.Element[] = routesData?.map((route: RouteObjReduced, index: number): JSX.Element => {
    return (
      <FeatureGroup
        key={index}
        eventHandlers={{
          mouseover: (event: LeafletMouseEvent) => {
            event.target.setStyle({ weight: 6 });
            event.target.bringToFront();
          },
          mouseout: (event: LeafletMouseEvent) => event.target.setStyle({ weight: 4 }),
        }}
      >
        <React.Fragment key={index}>
          {route?.shapesDirection_0 && route?.shapesDirection_0?.length
            ? route?.shapesDirection_0?.map((elem: LanLngPoint[], internalIndex: number) => {
                return PolylineToRender(route, elem, internalIndex, {
                  opacity: 1,
                  color: route?.background,
                  weight: 4,
                });
              })
            : null}
          {route?.shapesDirection_1 && route?.shapesDirection_1?.length
            ? route?.shapesDirection_1?.map((elem: LanLngPoint[], internalIndex: number) => {
                return PolylineToRender(route, elem, internalIndex, {
                  opacity: 1,
                  color: route?.background,
                  weight: 4,
                });
              })
            : null}
        </React.Fragment>
      </FeatureGroup>
    );
  });

  return (
    <React.Fragment>
      <Button variant="outline-success" onClick={handleShow} size="sm" disabled={disabled}>
        {t("Mapa")}
      </Button>

      <Modal show={show} onHide={closeAndReset} keyboard={true} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{t("Mapa przystanków")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DivMap>
            <MapContainer
              zoomSnap={0.1}
              zoomDelta={0.2}
              maxZoom={18}
              minZoom={6.5}
              bounds={initialMapRange}
              scrollWheelZoom={true}
              style={{ width: "100%", height: "100%" }}
              zoomControl={false}
              doubleClickZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ScaleControl position="bottomright" />

              {/* //* Stops + Routes */}
              {allStopsArrayFiltered?.length ? (
                <AllStopsCircleMarkers allStopsArrayFiltered={allStopsArrayFiltered} selectedStopId={selectedStopId} />
              ) : null}
              {routesData?.length ? allRoutesPolylinesViaStop : null}
            </MapContainer>
          </DivMap>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closeAndReset}>
            {t("Zamknij")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default StopPunctualityReportMapModal;

//* All Stops Component
export const AllStopsCircleMarkers = ({
  allStopsArrayFiltered,
  selectedStopId,
}: {
  allStopsArrayFiltered: StopInfo[];
  selectedStopId?: string;
}): JSX.Element => {
  const { t } = useTranslation();

  const stopsCircleMarkers: JSX.Element[] = allStopsArrayFiltered?.map((stop: StopInfo, index: number): JSX.Element => {
    return (
      <CircleMarker
        center={[stop?.lat as number, stop?.lon as number]}
        pathOptions={{
          fillColor: selectedStopId && stop?.stopId === selectedStopId ? dangerColor : primaryColor,
          color: selectedStopId && stop?.stopId === selectedStopId ? dangerColor : primaryColor,
          opacity: 1.0,
          fillOpacity: 0.8,
        }}
        radius={selectedStopId && stop?.stopId === selectedStopId ? 6 : 2}
        key={index + stop?.stopId}
      >
        <Popup>
          <PopupContainer>
            <div className="popup-line">
              {t("Nazwa")} <span className="span_maroon">{stop?.stopName}</span>
            </div>
            <div className="popup-line">
              {t("Id")} <span className="span_maroon">{stop?.stopId}</span>
            </div>
            <div className="popup-line text-muted small">
              {t("Pozycja")}: <span className="span_bold text-muted">{`${stop?.lat} ${stop?.lon}`}</span>
            </div>
          </PopupContainer>
          <StopAvailableRoutes stopId={stop?.stopId as string} showHeader={true} />
        </Popup>
      </CircleMarker>
    );
  });

  return <React.Fragment>{stopsCircleMarkers}</React.Fragment>;
};
